<template>
  <b-modal
    centered
    ref="form-modal"
    v-model="editorShown"
    :title="formId ? 'Edit Insurance Company' : 'Add Insurance Company'"
    header-bg-variant="primary"
    header-text-variant="white"
    @ok="submitInsuranceCompany"
    no-close-on-backdrop
    size="md-lg"
    header-class="bg-purple"
  >
    <template v-slot:modal-footer="{ ok, cancel }">
      <b-button variant="secondary" @click="cancel()">
        Cancel
      </b-button>
      <b-button variant="primary" @click="ok()">
        Save
      </b-button>
    </template>
    <b-row>
      <b-col>
        <b-form-group
          label="Name"
          :state="formErrors.name.length == 0 ? null : false"
          :invalid-feedback="formErrors.name.join(', ')"
        >
          <template v-slot:label>
            Name <span class="text-danger">*</span>
          </template>
          <b-form-input v-model="form.name" maxLength="100"></b-form-input>
        </b-form-group>
      </b-col>
      <b-col>
        <b-form-group label="Payer ID">
          <b-form-input v-model="form.payerId" maxLength="100"></b-form-input>
        </b-form-group>
      </b-col>
    </b-row>
    <b-row>
      <b-col>
        <b-form-group
          label="Mobile Phone Number"
          :state="formErrors.phone.length == 0 ? null : false"
          :invalid-feedback="formErrors.phone.join(', ')"
        >
          <phone-input
            ref="phoneInput"
            defaultCountry="US"
            v-model="form.phone"
            trim
            :state="formErrors.phone.length == 0 ? null : false"
          ></phone-input>
        </b-form-group>
      </b-col>
      <b-col>
        <b-form-group
          label="Email"
          :state="formErrors.email.length == 0 ? null : false"
          :invalid-feedback="formErrors.email.join(', ')"
        >
          <b-form-input
            v-model="form.email"
            maxLength="255"
            :state="formErrors.email.length == 0 ? null : false"
          ></b-form-input>
        </b-form-group>
      </b-col>
    </b-row>
    <b-row>
      <b-col cols="6">
        <b-form-group
          label="Fax"
          :state="formErrors.fax.length == 0 ? null : false"
          :invalid-feedback="formErrors.fax.join(', ')"
        >
          <phone-input
            ref="faxInput"
            defaultCountry="US"
            v-model="form.fax"
            trim
            :state="formErrors.fax.length == 0 ? null : false"
          ></phone-input>
        </b-form-group>
      </b-col>
    </b-row>
    <b-row>
      <b-col>
        <b-form-group label="Address Line 1">
          <b-form-input
            v-model="form.addressLine1"
            maxLength="255"
          ></b-form-input>
        </b-form-group>
      </b-col>
      <b-col>
        <b-form-group label="Address Line 2">
          <b-form-input
            v-model="form.addressLine2"
            maxLength="255"
          ></b-form-input>
        </b-form-group>
      </b-col>
    </b-row>
    <b-row>
      <b-col>
        <b-form-group label="City">
          <b-form-input v-model="form.city" maxLength="100"></b-form-input>
        </b-form-group>
      </b-col>
      <b-col>
        <b-form-group label="State">
          <v-select v-model="form.state" :options="states"></v-select>
        </b-form-group>
      </b-col>
    </b-row>
    <b-row>
      <b-col cols="6">
        <b-form-group
          label="Zipcode"
          :state="formErrors.zipcode.length == 0 ? null : false"
          :invalid-feedback="formErrors.zipcode.join(', ')"
        >
          <b-form-input
            v-model="form.zipcode"
            v-facade="'#####'"
            :state="formErrors.zipcode.length == 0 ? null : false"
          ></b-form-input>
        </b-form-group>
      </b-col>
    </b-row>
  </b-modal>
</template>

<script>
import insuranceCompanyServer from "../../server/insuranceCompany";
import { mapActions, mapState } from "vuex";
import PhoneInput from "../controls/PhoneInput.vue";
import {
  validateEmail,
  validatePhoneNumber,
  validateRequired,
  validateZipcode
} from "../../helpers/validation";

export default {
  name: "InsuranceCompanyModal",
  components: { PhoneInput },
  props: ["formId"],
  data() {
    return {
      list: [],
      form: {
        name: "",
        phone: "",
        payerId: "",
        email: "",
        fax: "",
        addressLine1: "",
        addressLine2: "",
        city: "",
        state: null,
        zipcode: ""
      },
      formErrors: {
        name: [],
        zipcode: [],
        phone: [],
        email: [],
        fax: []
      }
    };
  },
  async mounted() {
    this.fetchStates();
    if (this.formId) {
      const form = await insuranceCompanyServer.get(this.formId);
      this.form = {
        name: form.name,
        payerId: form.payerId,
        phone: form.phoneNumber,
        email: form.email,
        fax: form.fax,
        addressLine1: form.addressLine1,
        addressLine2: form.addressLine2,
        city: form.city,
        state: form.state
          ? {
              value: form.state.id,
              label: form.state.name
            }
          : null,
        zipcode: form.zipcode
      };
    }
  },
  methods: {
    ...mapActions("layout", ["showAlertSuccess", "showError"]),
    ...mapActions("client", ["fetchStates"]),
    validateSubmitInsuranceCompany() {
      let withoutErrors = true;

      this.formErrors.name = [];
      if (!validateRequired(this.form.name)) {
        this.formErrors.name.push("This field is required");
        withoutErrors = false;
      }

      this.formErrors.zipcode = [];
      if (this.form.zipcode && !validateZipcode(this.form.zipcode)) {
        this.formErrors.zipcode.push("This field must have a valid zipcode");
        withoutErrors = false;
      }

      this.formErrors.phone = [];
      if (
        this.form.phone.trim().length >
          this.activePhoneCountry.dialCode.length + 1 &&
        !validatePhoneNumber(this.form.phone)
      ) {
        this.formErrors.phone.push("Invalid phone number");
        withoutErrors = false;
      }

      this.formErrors.email = [];
      if (this.form.email && !validateEmail(this.form.email)) {
        this.formErrors.email.push("This field must have a valid email");
        withoutErrors = false;
      }

      this.formErrors.fax = [];
      if (
        this.form.fax.trim().length >
          this.activeFaxCountry.dialCode.length + 1 &&
        !validatePhoneNumber(this.form.fax)
      ) {
        this.formErrors.fax.push("Invalid phone number");
        withoutErrors = false;
      }

      return withoutErrors;
    },
    async submitInsuranceCompany(modalEvent) {
      modalEvent.preventDefault();
      const result = this.validateSubmitInsuranceCompany();
      if (result) {
        const formData = {
          name: this.form.name,
          payerId: this.form.payerId,
          fax:
            this.form.fax.trim().length >
            this.activeFaxCountry.dialCode.length + 1
              ? this.form.fax
              : "",
          phoneNumber:
            this.form.phone.trim().length >
            this.activePhoneCountry.dialCode.length + 1
              ? this.form.phone
              : "",
          email: this.form.email,
          addressLine1: this.form.addressLine1,
          addressLine2: this.form.addressLine2,
          city: this.form.city,
          stateId: this.form.state ? this.form.state.value : null,
          zipcode: this.form.zipcode
        };
        let insuranceCompany = {};
        try {
          if (this.formId) {
            await insuranceCompanyServer.update(this.formId, formData);
          } else {
            insuranceCompany = await insuranceCompanyServer.create(formData);
          }
          this.showAlertSuccess();
          this.$emit("onModify", { insuranceCompany: insuranceCompany });
          this.$refs["form-modal"].hide();
        } catch {
          this.showError({
            message: "An insurance company with this name already exists"
          });
        }
      }
    }
  },
  computed: {
    ...mapState("client", {
      states: state =>
        state.states.map(s => ({
          label: s.name,
          value: s.id
        }))
    }),
    editorShown: {
      get() {
        return !!this.$attrs.value;
      },
      set(value) {
        this.$emit("input", value);
      }
    },
    activePhoneCountry() {
      return this.$refs.phoneInput.$refs.telInput.activeCountry;
    },
    activeFaxCountry() {
      return this.$refs.faxInput.$refs.telInput.activeCountry;
    }
  }
};
</script>

<style scoped>
.section-title {
  margin-top: 25px;
  margin-bottom: 15px;
}
.section-body {
  min-height: 70px;
}
</style>
